import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import { useNotes } from '../../contexts/NotesContext';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import NoteShareModal from '../Notes/NoteShareModal';
import HeaderUserControls from './HeaderUserControls';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import i18n from '../../i18n';

// Helper function to format relative time
const formatRelativeTime = (date) => {
  const t = (key, opts) => i18n.t(`shared:header.${key}`, opts);
  if (!date) return t('justNow');
  try {
    const now = new Date();
    const target = new Date(date);
    const diffMs = now - target;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffSecs < 10) return t('justNow');
    if (diffSecs < 60) return t('secondsAgo', { count: diffSecs });
    if (diffMins < 60) return t('minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('hoursAgo', { count: diffHours });
    if (diffDays === 1) return t('yesterday');
    if (diffDays < 7) return t('daysAgo', { count: diffDays });
    const dateLocale = getDateFnsLocale(i18n.language);
    return formatDistanceToNow(target, { addSuffix: true, locale: dateLocale });
  } catch (error) {
    return t('justNow');
  }
};

/**
 * Header çevrimdışı göstergesi: `[WifiOff] Çevrimdışı` + hover tooltip.
 * Tooltip metni eski genel banner metnidir (`shared:connectionBanner.message`).
 * Tooltip saf CSS (group-hover) — yeni bir tooltip altyapısı kurulmadı.
 */
const OfflineIndicator = () => {
  const { t } = useTranslation('shared');
  const description = t('connectionBanner.message');

  return (
    <div className="relative group" role="status" aria-live="polite" aria-label={description}>
      <span className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-red-500/25 bg-red-500/10 text-[11px] font-medium text-red-300 cursor-default select-none">
        <WifiOff className="w-3.5 h-3.5 text-red-400 shrink-0" aria-hidden />
        {t('header.offline')}
      </span>
      <div className="pointer-events-none absolute end-0 top-full mt-2 w-[22rem] max-w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-white/10 bg-[#0d1424] px-3 py-2 text-[11px] leading-snug text-[#c8d8eb] shadow-[0_12px_32px_rgba(0,0,0,0.55)] opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
        {description}
      </div>
    </div>
  );
};

const Header = ({ onSectionChange }) => {
  const { t } = useTranslation('shared');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const { selectedNote, duplicateNote, deleteNote, selectNote, updateNote } = useNotes();
  const { isServerOnline } = useConnectionMonitor();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [pendingTitle, setPendingTitle] = useState(null);
  const titleInputRef = useRef(null);
  const moreMenuRef = useRef(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Initialize editing title when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Initialize editing title value
  // [selectedNote] yerine [id, title] izlenince auto-save sonrası updatedAt değişiminde
  // kullanıcının yazmakta olduğu başlık sıfırlanmıyor; !isEditingTitle guard da aynı amaca hizmet eder
  useEffect(() => {
    if (selectedNote && !isEditingTitle) {
      setEditingTitle(selectedNote.title || '');
    }
  }, [selectedNote?.id, selectedNote?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  // API/autosave yarışı bitince optimistik başlığı temizle
  useEffect(() => {
    if (!pendingTitle || !selectedNote) return;
    const contextTitle = selectedNote.title || tCommon('unnamed');
    if (contextTitle === pendingTitle) {
      setPendingTitle(null);
    }
  }, [selectedNote?.title, selectedNote?.id, pendingTitle, tCommon]);

  useEffect(() => {
    setPendingTitle(null);
  }, [selectedNote?.id]);

  // Get note category/tag for breadcrumb
  const getNoteCategory = () => {
    if (!selectedNote || !selectedNote.category) {
      return null;
    }
    
    // Category can be an object {name, color} or a string
    if (typeof selectedNote.category === 'string') {
      return selectedNote.category;
    }
    
    if (selectedNote.category && selectedNote.category.name) {
      return selectedNote.category.name;
    }
    
    return null;
  };

  const handleEditClick = () => {
    setIsEditMode(!isEditMode);
    setIsPreviewMode(false);
    // TODO: Trigger edit mode in editor
  };

  const handlePreviewClick = () => {
    setIsPreviewMode(!isPreviewMode);
    setIsEditMode(false);
    // TODO: Trigger preview mode in editor
  };

  const handleShareClick = () => {
    if (selectedNote) {
      setShowShareModal(true);
    }
  };

  const handleMoreClick = () => {
    setShowMoreMenu(!showMoreMenu);
  };

  const handleDuplicateClick = async () => {
    if (!selectedNote) return;
    setShowMoreMenu(false);
    try {
      const duplicated = await duplicateNote(selectedNote.id);
      if (duplicated) {
        selectNote(duplicated);
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'success', title: t('header.duplicateSuccessTitle'), message: t('header.duplicateSuccessMessage') },
          })
        );
      }
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('header.errorTitle'), message: t('header.duplicateErrorMessage') },
        })
      );
    }
  };

  const handleDeleteClick = () => {
    setShowMoreMenu(false);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedNote) return;
    try {
      // Çevrimdışıyken deleteNote false döner ve kendi uyarısını gösterir
      const deleted = await deleteNote(selectedNote.id);
      setShowDeleteConfirm(false);
      if (deleted === false) return;
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'success', title: t('header.duplicateSuccessTitle'), message: t('header.deleteSuccessMessage') },
        })
      );
    } catch (error) {
      setShowDeleteConfirm(false);
      if (error?.payload?.error === 'MUST_TRANSFER_OWNERSHIP') {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'error', title: t('header.errorTitle'), message: t('header.deleteOwnershipErrorMessage') },
          })
        );
      } else {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'error', title: t('header.errorTitle'), message: t('header.deleteErrorMessage') },
          })
        );
      }
    }
  };

  // Ortak çalışma notu çevrimdışıyken salt okunur; kişisel notun başlığı düzenlenebilir
  const isOfflineSharedNote =
    !isServerOnline && !!(selectedNote?.isCollaborative || selectedNote?.myRole === 'editor');

  const handleTitleClick = () => {
    if (selectedNote && !isOfflineSharedNote) {
      setIsEditingTitle(true);
      setEditingTitle(selectedNote.title || '');
    }
  };

  const handleTitleBlur = () => {
    if (!selectedNote) return;
    if (isOfflineSharedNote) {
      setEditingTitle(selectedNote.title || '');
      setIsEditingTitle(false);
      return;
    }
    const newTitle = editingTitle.trim() || tCommon('unnamed');
    if (newTitle !== (selectedNote.title || tCommon('unnamed'))) {
      setPendingTitle(newTitle);
      window.dispatchEvent(new CustomEvent('notesplus-note-title-sync', {
        detail: { noteId: selectedNote.id, title: newTitle },
      }));
      updateNote({
        ...selectedNote,
        title: newTitle,
      }).catch(() => setPendingTitle(null));
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      titleInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditingTitle(selectedNote?.title || '');
      setIsEditingTitle(false);
    }
  };

  // Get page-specific header content based on route
  const getPageHeader = () => {
    const path = location.pathname;
    
    // Notes page - show note information
    if (path === '/notes' || path === '/') {
      if (!selectedNote) {
        return null; // Don't show header if no note is selected
      }
      
      const relativeTime = formatRelativeTime(selectedNote.updatedAt || selectedNote.createdAt);
      const noteCategory = getNoteCategory();
      const displayTitle = pendingTitle ?? selectedNote.title ?? tCommon('unnamed');

      return {
        breadcrumbs: null,
        title: isEditingTitle ? (
          <input
            ref={titleInputRef}
            type="text"
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            className="text-lg font-bold text-slate-900 dark:text-white leading-tight bg-transparent border-b-2 border-primary focus:outline-none focus:border-primary"
            style={{ minWidth: '200px' }}
          />
        ) : (
          <h2
            onClick={handleTitleClick}
            className="text-lg font-bold text-slate-900 dark:text-white leading-tight cursor-text hover:text-primary transition-colors"
            title={t('header.editTitleTooltip')}
          >
            {displayTitle || tCommon('unnamed')}
          </h2>
        ),
        rightSection: (
          <span className="text-xs text-slate-400 dark:text-slate-400">{t('header.lastEdited', { time: relativeTime })}</span>
        ),
      };
    }

    // Profile page
    if (path === '/profile') {
      return {
        breadcrumbs: (
          <>
            <span>{t('header.breadcrumbSettings')}</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span>{t('header.breadcrumbProfile')}</span>
          </>
        ),
        title: <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{t('header.accountInfoTitle')}</h2>,
        rightSection: null,
      };
    }

    // Settings page
    if (path === '/settings') {
      return {
        breadcrumbs: (
          <>
            <span>{t('header.breadcrumbApp')}</span>
            <span className="mx-1">›</span>
            <span>{t('header.settingsTitle')}</span>
          </>
        ),
        title: <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{t('header.settingsTitle')}</h2>,
        rightSection: null,
      };
    }

    // Calendar page
    if (path === '/calendar') {
      return {
        breadcrumbs: (
          <>
            <span>{t('header.breadcrumbApp')}</span>
            <span className="mx-1">›</span>
            <span>{t('header.calendarTitle')}</span>
          </>
        ),
        title: <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{t('header.calendarTitle')}</h2>,
        rightSection: null,
      };
    }

    // Chat page
    if (path === '/chat') {
      return {
        breadcrumbs: (
          <>
            <span>{t('header.breadcrumbApp')}</span>
            <span className="mx-1">›</span>
            <span>{t('header.chatTitle')}</span>
          </>
        ),
        title: <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{t('header.chatTitle')}</h2>,
        rightSection: null,
      };
    }

    // Break Reminder page
    if (path === '/break-reminder') {
      return {
        breadcrumbs: (
          <>
            <span>{t('header.breadcrumbApp')}</span>
            <span className="mx-1">›</span>
            <span>{t('header.breakReminderTitle')}</span>
          </>
        ),
        title: <h2 className="text-lg font-bold text-slate-900 dark:text-white leading-tight">{t('header.breakReminderTitle')}</h2>,
        rightSection: null,
      };
    }
    
    // Default - no header
    return null;
  };

  const pageHeader = getPageHeader();

  return (
    <header className="h-16 border-b border-slate-200 dark:border-white/5 flex items-center justify-between px-6 bg-white dark:bg-[var(--color-bg-base)] shrink-0 z-30">
      {/* Hamburger + Main Header Content */}
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          {pageHeader?.title}
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Çevrimdışıyken sayfa bilgisinin ("Son düzenleme") yerini durum göstergesi alır;
            bağlantı dönünce normal header bilgisi kendiliğinden geri gelir. Tüm sekmelerde görünür. */}
        {isServerOnline ? pageHeader?.rightSection : <OfflineIndicator />}
        {(!isServerOnline || pageHeader?.rightSection) && (
          <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
        )}
        <HeaderUserControls onSectionChange={onSectionChange} />
      </div>

      {/* Share Modal */}
      <NoteShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        note={selectedNote}
      />

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
            >
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                {t('header.deleteNoteTitle')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                {t('header.deleteNoteConfirm', { title: selectedNote?.title || tCommon('unnamed') })}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  {t('header.cancel')}
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  {t('header.delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;

